import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import Markdown from "@/components/Markdown";
import { api } from "@/lib/api";
import { blogCoverUrl } from "@/pages/LearnMore";

export default function BlogArticle() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | ok | notfound

  useEffect(() => {
    let alive = true;
    setStatus("loading");
    api.get(`/blogs/${slug}`)
      .then(({ data }) => { if (alive) { setArticle(data); setStatus("ok"); } })
      .catch(() => { if (alive) setStatus("notfound"); });
    return () => { alive = false; };
  }, [slug]);

  const cover = blogCoverUrl(article);

  return (
    <div className="min-h-screen bg-white dark:bg-slate-950 flex flex-col" data-testid="blog-article-page">
      <Header />
      <main className="flex-1 w-full">
        <div className="max-w-3xl mx-auto px-6 lg:px-10 py-12 sm:py-16">
          <Link
            to="/learn-more"
            data-testid="blog-back-link"
            className="inline-flex items-center gap-1.5 text-sm text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" /> Back to Learn more
          </Link>

          {status === "loading" && (
            <div className="mt-8 space-y-4 animate-pulse" data-testid="blog-article-loading">
              <div className="h-8 bg-slate-100 dark:bg-slate-800 rounded w-3/4" />
              <div className="aspect-[16/9] bg-slate-100 dark:bg-slate-800 rounded-xl" />
              <div className="h-4 bg-slate-100 dark:bg-slate-800 rounded" />
              <div className="h-4 bg-slate-100 dark:bg-slate-800 rounded w-5/6" />
            </div>
          )}

          {status === "notfound" && (
            <div className="mt-16 text-center" data-testid="blog-article-notfound">
              <h1 className="font-display text-2xl font-semibold text-slate-900 dark:text-slate-50">Article not found</h1>
              <p className="mt-3 text-sm text-slate-600 dark:text-slate-300">
                This article may have been unpublished or moved.
              </p>
              <Link to="/learn-more" className="mt-6 inline-flex items-center gap-2 h-10 px-5 rounded-md bg-slate-900 dark:bg-teal-600 text-white text-sm font-medium hover:bg-slate-800 dark:hover:bg-teal-500 transition-colors">
                Browse all articles <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          )}

          {status === "ok" && article && (
            <article className="mt-8" data-testid="blog-article">
              <p className="text-[11px] uppercase tracking-[0.18em] text-teal-700 dark:text-teal-400">Guide</p>
              <h1 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl font-semibold text-slate-900 dark:text-slate-50 tracking-tight leading-tight">
                {article.title}
              </h1>
              {article.excerpt && (
                <p className="mt-4 text-lg text-slate-600 dark:text-slate-300 leading-relaxed">
                  {article.excerpt}
                </p>
              )}
              {cover && (
                <div className="mt-8 rounded-xl overflow-hidden border border-slate-200 dark:border-slate-800">
                  <img src={cover} alt={article.title} className="w-full object-cover" data-testid="blog-article-cover" />
                </div>
              )}
              <Markdown className="mt-10">{article.content}</Markdown>

              {article.source_url && (
                <p className="mt-10 text-xs text-slate-400 dark:text-slate-500">
                  Originally published at{" "}
                  <a href={article.source_url} target="_blank" rel="noopener noreferrer" className="underline underline-offset-2 hover:text-slate-600 dark:hover:text-slate-300">
                    the source
                  </a>.
                </p>
              )}

              {/* Conversion CTA */}
              <div className="mt-12 rounded-xl border border-teal-200 dark:border-teal-500/30 bg-teal-50/60 dark:bg-teal-500/10 p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                  <p className="font-display text-base font-semibold text-slate-900 dark:text-slate-50">Ready to find your gaps?</p>
                  <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">Run a free Biz Resilience Scan and get a plain-English plan in minutes.</p>
                </div>
                <Link
                  to="/mode"
                  data-testid="blog-cta-start"
                  className="inline-flex items-center gap-2 h-10 px-5 rounded-md bg-slate-900 dark:bg-teal-600 text-white text-sm font-medium hover:bg-slate-800 dark:hover:bg-teal-500 transition-colors whitespace-nowrap"
                >
                  Start free scan <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </article>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
